import { useState, useEffect } from 'react';
import { campanasMasterService, ConsolidadoZona } from '../../services/campanasMasterService';
import { CampanaMaster } from '../../lib/supabase';
import { formatCurrency } from '../../utils/formatters';
import { ProgresoBar, calcularProgreso } from '../../utils/progresoUtils';
import { NIVEL_CONFIG } from '../../utils/campanaConstants';
import { StatusBadge } from '../ui/StatusBadge';
import { EditarObjetivosZonaModal } from './EditarObjetivosZonaModal';
import { FormErrorAlert } from '../ui/FormErrorAlert';
import { reportarErrorInesperado } from '../../utils/reportarErrorInesperado';
import { useAuth } from '../../context/AuthContext';
import { puedeAdministrarMaestra } from '../../utils/permissions';
import { Button } from '../ui/Button';
import {
  ChevronDown,
  ChevronRight,
  Building2,
  Target,
  Coins,
  BarChart3,
  Eye,
  Lock,
  ArrowDown,
  ArrowUp,
  AlertCircle,
  Pencil,
} from 'lucide-react';

interface CampanasMasterPanelProps {
  readonly nivel: 'global' | 'nacional' | 'diocesano';
  readonly provinciaEclesiasticaId?: string;
  readonly diocesisId?: string;
}

const GRID_COLS = '1fr 80px 100px 100px 80px';
const colorProgreso = (pct: number) => {
  if (pct >= 100) return 'text-emerald-600';
  return pct >= 50 ? 'text-amber-600' : 'text-gray-600';
};

function CampanaMasterCard({
  campana: inicial,
  opciones,
  readOnly,
}: {
  readonly campana: CampanaMaster;
  readonly opciones: { readonly diocesisId?: string; readonly provinciaEclesiasticaId?: string };
  readonly readOnly: boolean;
}) {
  const { usuario } = useAuth();
  const [estadoActual, setEstadoActual] = useState<CampanaMaster['estado'] | null>(null);
  const campana = { ...inicial, estado: estadoActual ?? inicial.estado };
  const [guardandoEstado, setGuardandoEstado] = useState(false);
  const [errorEstado, setErrorEstado] = useState<string | null>(null);
  const puedeAdministrar = !readOnly && puedeAdministrarMaestra(usuario, campana);
  const puedeEditar = puedeAdministrar && ['planificacion', 'en_curso'].includes(campana.estado);
  const [expanded, setExpanded] = useState(false);
  const [consolidado, setConsolidado] = useState<ConsolidadoZona | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const nivelCfg = NIVEL_CONFIG[campana.nivel_origen];
  const cambiarEstado = async (estado: 'en_curso' | 'finalizada' | 'archivada') => {
    if (!puedeAdministrar || guardandoEstado) return;
    setGuardandoEstado(true); setErrorEstado(null);
    try {
      const actual = await campanasMasterService.getById(campana.id);
      const estadoEsperado = { en_curso: 'planificacion', finalizada: 'en_curso', archivada: 'finalizada' } as const;
      if (!actual || !puedeAdministrarMaestra(usuario, actual)
        || actual.estado !== estadoEsperado[estado]) throw new Error('La convocatoria cambió.');
      const actualizada = await campanasMasterService.update(actual.id, { estado });
      setEstadoActual(actualizada.estado);
    } catch { setErrorEstado('No pudimos actualizar la convocatoria. Revisa su estado antes de reintentar.'); }
    finally { setGuardandoEstado(false); }
  };

  const toggle = async () => {
    if (expanded) {
      setExpanded(false);
      setLoadError(false);
      return;
    }
    setExpanded(true);
    if (!consolidado) {
      setLoading(true);
      setLoadError(false);
      try {
        const data = await campanasMasterService.getConsolidadoPorZona(campana.id, opciones);
        setConsolidado(data);
      } catch (err) {
        console.error('Error cargando consolidado:', err);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="border border-sky-200 rounded-xl overflow-hidden bg-sky-50/30">
      <button onClick={toggle} className="w-full text-left hover:bg-sky-50 transition-colors">
        <div className="grid p-4" style={{ gridTemplateColumns: '1fr auto', alignItems: 'center', gap: '1rem' }}>
          <div className="flex items-start gap-3 min-w-0">
            <div className="flex-shrink-0 mt-0.5">
              {expanded
                ? <ChevronDown className="w-4 h-4 text-sky-500" />
                : <ChevronRight className="w-4 h-4 text-sky-500" />
              }
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 ${nivelCfg.badgeClass}`}>
                  {nivelCfg.icon}{nivelCfg.label}
                </span>
                <StatusBadge estado={campana.estado} tipo="actividad" />
                {readOnly && (
                  <span className="text-xs bg-gray-100 text-gray-500 border border-gray-200 font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Solo lectura
                  </span>
                )}
              </div>
              <p className="font-bold text-gray-900 truncate">{campana.nombre}</p>
              {campana.descripcion && (
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{campana.descripcion}</p>
              )}
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                <span>Objetivo global: <span className="font-semibold text-blue-700">{formatCurrency(campana.objetivo_global)}</span></span>
                {campana.fecha_inicio && (
                  <span>{campana.fecha_inicio}{campana.fecha_fin ? ` – ${campana.fecha_fin}` : ''}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sky-500 text-xs flex-shrink-0">
            <Eye className="w-3.5 h-3.5" />
            <span className="font-medium">Ver estado en mi zona</span>
          </div>
        </div>
      </button>
      <FormErrorAlert error={errorEstado} />
      {puedeEditar && <div className="flex flex-wrap gap-3 px-4 pb-4">
        {campana.estado === 'planificacion' && <Button size="sm" disabled={guardandoEstado}
          onClick={() => void cambiarEstado('en_curso')}>Iniciar convocatoria</Button>}
        {campana.estado === 'en_curso' && <Button variant="secondary" size="sm" disabled={guardandoEstado}
          onClick={() => void cambiarEstado('finalizada')}>Finalizar convocatoria</Button>}
        <p className="text-xs text-slate-600">Finalizar cierra compromisos nuevos y permite terminar la contabilidad.</p>
      </div>}
      {puedeAdministrar && campana.estado === 'finalizada' && <div className="px-4 pb-4">
        <Button variant="secondary" size="sm" disabled={guardandoEstado}
          onClick={() => void cambiarEstado('archivada')}>Archivar convocatoria conservando su historia</Button>
      </div>}

      {expanded && (
        <div className="border-t border-sky-200 bg-white">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            </div>
          )}
          {!loading && loadError && (
            <div className="flex items-center justify-center gap-2 py-8 text-red-500 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>Error al cargar los datos. Cierra y vuelve a intentarlo.</span>
            </div>
          )}
          {!loading && !loadError && consolidado && (
            <div className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                    <Building2 className="w-3 h-3" /> Sedes en tu zona
                  </div>
                  <p className="text-xl font-bold text-gray-900">{consolidado.totalLumisiales}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <div className="flex items-center gap-1.5 text-xs text-blue-600 mb-1">
                    <Target className="w-3 h-3" /> Objetivo en tu zona
                  </div>
                  <p className="text-xl font-bold text-blue-700">{formatCurrency(consolidado.totalObjetivoZona)}</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                  <div className="flex items-center gap-1.5 text-xs text-emerald-600 mb-1">
                    <Coins className="w-3 h-3" /> Recaudado en tu zona
                  </div>
                  <p className="text-xl font-bold text-emerald-700">{formatCurrency(consolidado.totalRecaudadoZona)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1.5">
                    <BarChart3 className="w-3 h-3" /> Progreso en tu zona
                  </div>
                  <p className="text-xl font-bold text-gray-900 mb-1">{consolidado.porcentajeZona}%</p>
                  <ProgresoBar
                    recaudado={consolidado.totalRecaudadoZona}
                    objetivo={consolidado.totalObjetivoZona}
                    heightClass="h-1.5"
                  />
                </div>
              </div>
              {puedeEditar && (
                <div className="flex justify-end mb-3">
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); if (puedeEditar) setEditModalOpen(true); }}
                    className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white text-sm font-semibold rounded-lg hover:bg-sky-700 transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Editar metas de mi zona
                  </button>
                </div>
              )}
              {consolidado.lumisiales.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="grid bg-gray-50 border-b border-gray-200" style={{ gridTemplateColumns: GRID_COLS }}>
                    <div className="py-2 pl-4 text-xs font-medium text-gray-500">Sede</div>
                    <div className="py-2 text-center text-xs font-medium text-gray-500">Diócesis</div>
                    <div className="py-2 text-center text-xs font-medium text-blue-600 flex items-center justify-center gap-1">
                      <Target className="w-3 h-3" /> Objetivo
                    </div>
                    <div className="py-2 text-center text-xs font-medium text-emerald-600 flex items-center justify-center gap-1">
                      <Coins className="w-3 h-3" /> Recaudado
                    </div>
                    <div className="py-2 pr-4 text-center text-xs font-medium text-gray-500 flex items-center justify-center gap-1">
                      <BarChart3 className="w-3 h-3" /> %
                    </div>
                  </div>
                  {consolidado.lumisiales.map((lum, idx) => {
                    const pct = calcularProgreso(lum.recaudado, lum.objetivo);
                    return (
                      <div
                        key={idx}
                        className="grid hover:bg-gray-50 transition-colors border-t border-gray-100"
                        style={{ gridTemplateColumns: GRID_COLS, alignItems: 'center' }}
                      >
                        <div className="py-2.5 pl-4 min-w-0">
                          <p className="font-medium text-gray-800 text-sm truncate">{lum.nombre}</p>
                          {lum.ciudad && <p className="text-xs text-gray-400 truncate">{lum.ciudad}</p>}
                        </div>
                        <div className="text-center py-2.5 text-xs text-gray-500 truncate px-1">{lum.diocesisNombre}</div>
                        <div className="text-center py-2.5 pr-2">
                          <span className="text-sm font-medium text-blue-700">{formatCurrency(lum.objetivo)}</span>
                        </div>
                        <div className="text-center py-2.5 pr-2">
                          <span className="text-sm font-medium text-emerald-700">{formatCurrency(lum.recaudado)}</span>
                        </div>
                        <div className="text-center py-2.5 pr-4">
                          <span className={`text-sm font-bold ${colorProgreso(pct)}`}>
                            {pct.toFixed(0)}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {!loading && !loadError && !consolidado && (
            <div className="py-6 text-center text-gray-400 text-sm">
              Esta campaña no tiene sedes asignadas en tu zona
            </div>
          )}
        </div>
      )}
      {consolidado && editModalOpen && puedeEditar && (
        <EditarObjetivosZonaModal
          isOpen={editModalOpen}
          consolidado={consolidado}
          onClose={() => setEditModalOpen(false)}
          onSuccess={() => {
            setConsolidado(null);
            setEditModalOpen(false);
          }}
        />
      )}
    </div>
  );
}

export function CampanasMasterPanel({ nivel, provinciaEclesiasticaId, diocesisId }: CampanasMasterPanelProps) {
  const { usuario } = useAuth();
  const [propias, setPropias] = useState<CampanaMaster[]>([]);
  const [loadingPropias, setLoadingPropias] = useState(true);
  const [loadingUp, setLoadingUp] = useState(true);
  const [loadingDown, setLoadingDown] = useState(true);
  const [campanasSuperior, setCampanasSuperior] = useState<CampanaMaster[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [campanasInferior, setCampanasInferior] = useState<CampanaMaster[]>([]);

  useEffect(() => {
    let vigente = true;
    campanasMasterService.getAll().then(datos => {
      if (vigente) setPropias(datos.filter(c => c.nivel_origen === nivel && c.estado !== 'archivada'
        && puedeAdministrarMaestra(usuario, c)));
    }).catch(() => { if (vigente) setError('No pudimos consultar las campañas propias.'); })
      .finally(() => { if (vigente) setLoadingPropias(false); });
    return () => { vigente = false; };
  }, [nivel, usuario]);

  useEffect(() => {
    const loadSuperior = async () => {
      if (nivel === 'global') { setLoadingUp(false); return; }
      try {
        const data = await campanasMasterService.getCampanasMasterVisibles({ nivel, provinciaEclesiasticaId, diocesisId, direccion: 'superior' });
        setCampanasSuperior(data);
      } catch (err) {
        // Sin esto la pantalla decía "no hay campañas activas", que no es lo mismo que "no se
        // pudieron cargar": el usuario concluía que no le toca ninguna campaña.
        setError('No pudimos cargar las campañas de otros niveles.');
        reportarErrorInesperado(err, 'No pudimos cargar las campañas de otros niveles.');
      } finally {
        setLoadingUp(false);
      }
    };
    const loadInferior = async () => {
      if (nivel === 'diocesano') { setLoadingDown(false); return; }
      try {
        const data = await campanasMasterService.getCampanasMasterVisibles({ nivel, provinciaEclesiasticaId, diocesisId, direccion: 'inferior' });
        setCampanasInferior(data);
      } catch (err) {
        setError('No pudimos cargar las campañas de otros niveles.');
        reportarErrorInesperado(err, 'No pudimos cargar las campañas de otros niveles.');
      } finally {
        setLoadingDown(false);
      }
    };
    loadSuperior();
    loadInferior();
  }, [nivel, provinciaEclesiasticaId, diocesisId]);

  const opciones = { diocesisId, provinciaEclesiasticaId };
  if (loadingPropias || loadingUp || loadingDown) {
    return <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  }
  if (propias.length === 0 && campanasSuperior.length === 0 && campanasInferior.length === 0) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-lg font-bold text-gray-900">Campañas Internacionales, Nacionales y Diocesanas</h2>
        </div>
        {/* La lista vacía puede significar dos cosas opuestas: que no hay campañas, o que no se
            pudieron leer. Solo se afirma lo primero cuando la carga sí funcionó. */}
        <FormErrorAlert error={error} />
        {!error && (
        <div className="text-center py-10 text-gray-400">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="font-medium">No hay campañas activas asignadas a tu zona</p>
          <p className="text-sm mt-1">Las campañas de otros niveles aparecerán aquí cuando sean distribuidas</p>
        </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-gray-900">Campañas Internacionales, Nacionales y Diocesanas</h2>
      <FormErrorAlert error={error} />
      {propias.length > 0 && <div className="space-y-3">
        <h3 className="font-semibold">Convocatorias de tu nivel</h3>
        <p className="text-sm text-slate-600">Finalizada cierra nuevos compromisos y permite terminar la contabilidad pendiente.</p>
        {propias.map(c => <CampanaMasterCard key={c.id} campana={c} opciones={opciones} readOnly={false} />)}
      </div>}
      {campanasSuperior.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <ArrowDown className="w-4 h-4 text-sky-500" />
            <h2 className="text-lg font-bold text-gray-900">Campañas distribuidas desde niveles superiores</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">Campañas creadas por niveles superiores que incluyen sedes de tu zona.</p>
          <div className="space-y-3">
            {campanasSuperior.map(c => <CampanaMasterCard key={c.id} campana={c} opciones={opciones} readOnly={true} />)}
          </div>
        </div>
      )}
      {campanasInferior.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <ArrowUp className="w-4 h-4 text-teal-500" />
            <h2 className="text-lg font-bold text-gray-900">Campañas creadas por niveles inferiores</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">Campañas originadas en niveles inferiores dentro de tu zona. Solo puedes consultar su estado.</p>
          <div className="space-y-3">
            {campanasInferior.map(c => <CampanaMasterCard key={c.id} campana={c} opciones={opciones}
              readOnly={!puedeAdministrarMaestra(usuario, c)} />)}
          </div>
        </div>
      )}
    </div>
  );
}
